import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createResizeObserver } from '../utils/observer';
import { getSize } from '../utils/iterable/getSize';

const FACTOR = 1.5;

function getElementTop({ container, element, gap }) {
  const { offsetTop } = element;
  const { clientHeight, scrollHeight } = container;
  return Math.max(0, Math.min(offsetTop - gap, scrollHeight - clientHeight));
}

function isElementAtTheTop({ container, element, gap }) {
  if (!element || !container) return undefined;

  const { offsetHeight, offsetTop } = element;
  const { clientHeight, scrollHeight, scrollTop } = container;

  function getMultiplier() {
    return ((scrollTop - scrollHeight) + clientHeight * FACTOR) / (clientHeight * FACTOR - clientHeight);
  }

  const top = scrollTop < scrollHeight - clientHeight * FACTOR
    ? scrollTop + gap
    : scrollTop + gap + (clientHeight - gap) * getMultiplier();

  return offsetTop <= top && offsetTop + offsetHeight >= top;
}

export function useFormTabs({ container, forms, gap = 10, refs }) {
  const [activeTab, setTab] = useState(0);
  const activeRef = useRef();
  const timeoutId = useRef();
  const waitForSmoothScroll = useRef(false);
  const stopWaitingForScroll = useCallback(
    function callback() {
      if (waitForSmoothScroll.current) {
        waitForSmoothScroll.current = false;
      }
    },
    [],
  );

  const setActiveTab = useCallback(
    function callback(form, index, smooth = true) {
      if (container.current) {
        if (form in refs.current) {
          const { clientHeight, scrollHeight, scrollTop } = container.current;

          if (scrollHeight > clientHeight) {
            const top = getElementTop({
              container: container.current,
              element: refs.current[form],
              gap,
            });

            if (top !== scrollTop) {
              if (smooth) {
                waitForSmoothScroll.current = true;
                container.current.scrollTo({ top, behavior: 'smooth' });
              } else {
                container.current.scrollTop = top;
              }

            } else if (waitForSmoothScroll.current) {
              stopWaitingForScroll();
            }
          } else if (waitForSmoothScroll.current) {
            stopWaitingForScroll();
          }
        }
      }

      activeRef.current = index;
      setTab(activeRef.current);
    },
    [gap],
  );

  useEffect(
    function effect() {
      if (!container.current || !refs.current) {
        return undefined;
      }

      function updateSelected() {
        if (waitForSmoothScroll.current) {
          if (timeoutId.current) {
            timeoutId.current = clearTimeout(timeoutId.current);
          }

          timeoutId.current = setTimeout(stopWaitingForScroll, 100);

          return;
        }

        if (waitForSmoothScroll.current) return;

        for (let i = 0; i < getSize(forms); i += 1) {
          const form = refs.current[forms[i]];

          if (form && isElementAtTheTop({ container: container.current, element: form, gap })) {
            if (activeRef.current !== i) {
              activeRef.current = i;
              setTab(activeRef.current);
            }

            return;
          }
        }
      }

      const observer = createResizeObserver(updateSelected);

      for (let i = 0; i < getSize(forms); i += 1) {
        const form = refs.current[forms[i]];

        if (form) {
          observer.observe(form);
        }
      }

      container.current.addEventListener('mousedown', stopWaitingForScroll);
      container.current.addEventListener('keydown', stopWaitingForScroll);
      container.current.addEventListener('wheel', stopWaitingForScroll);
      container.current.addEventListener('scroll', updateSelected);

      return function cleanUp() {
        observer.disconnect();

        if (container.current) {
          container.current.removeEventListener('mousedown', stopWaitingForScroll);
          container.current.removeEventListener('keydown', stopWaitingForScroll);
          container.current.removeEventListener('wheel', stopWaitingForScroll);
          container.current.removeEventListener('scroll', updateSelected);
        }
      };
    },
    [container, forms, gap]
  );

  return useMemo(
    function factory() {
      return [activeTab, setActiveTab];
    },
    [activeTab, setActiveTab],
  );
}
